/**
 * The UC1 scorecard's five rows, as data.
 *
 * The client's §5 asks for one tick/cross row per constraint carrying the measured value
 * and the threshold it was compared against ("Rainfall 2025: 412 mm — lowest third for
 * Burkina Faso ✓ flagged"), and the "Why is this area highlighted?" block asks for the
 * flagged ones restated as a sentence each. Those are the same sentence, so it is built
 * once here and rendered twice (`SelectedArea.tsx`, `Why.tsx`) — two builders would be two
 * chances for the scorecard and the explanation to disagree about the same number.
 *
 * Pure: `t` comes in as an argument, so the whole wording is testable without a React tree,
 * and no string in this module is written in English.
 */
import type { IntelDriver } from '../../lib/api';
import { fmtNum } from '../../lib/format';
import { UC1_DIMENSIONS, UC1_TERCILE_CLASS } from './uc1Constants';

/** Just enough of i18next's `t` for this module — key plus interpolation values. */
export type Translate = (key: string, options?: Record<string, unknown>) => string;

export type Uc1RowStatus = 'flagged' | 'not_flagged' | 'not_assessed';

export interface Uc1Row {
  dimension: string;
  status: Uc1RowStatus;
  /** ✓ flagged, ✗ not flagged, – not assessed (client §5). */
  icon: string;
  /** The icon's accessible name, since a tick is not a word. */
  iconLabel: string;
  /** The row sentence up to the verdict word. */
  text: string;
  /**
   * What follows the verdict word, when the row reads better with the verdict inside the
   * sentence than appended to it. A not-assessed row is the case: "Productivity: not
   * assessed — no survey value" printed the status word twice, once in the sentence and
   * once in `.uc1-row-status` after it. The word is now printed once, by that span, with
   * the reason on this side of it. Empty on a flagged row, which reads "… ✓ flagged".
   */
  tail?: string;
  /** Productivity's GAEZ yield-gap line — shown, never scored. */
  evidence?: string;
  /** Climate's 2025 anomaly line, present whether or not the normal exists yet. */
  subLine?: string;
}

const ICONS: Record<Uc1RowStatus, string> = {
  flagged: '✓',
  not_flagged: '✗',
  not_assessed: '–',
};

/** `values` as this pane reads it — everything else on the object is left alone. */
export interface Uc1Values {
  rain_anom_2025?: number | null;
  main_crops?: { crop: string; share: number }[];
  rollup?: {
    n_watersheds?: number;
    n_assessed?: Record<string, number | null>;
    ag_ha_flagged?: number;
    share_flagged?: Record<string, number | null>;
  };
  [key: string]: unknown;
}

const num = (v: unknown, decimals = 1): string =>
  typeof v === 'number' ? fmtNum(v, decimals) : String(v ?? '—');

/** "412 mm", or "412" when the metric carries no unit. */
function withUnit(value: unknown, unit: string | null | undefined): string {
  const text = num(value);
  return unit ? `${text} ${unit}` : text;
}

/**
 * The constraint's name, with its own vintage appended when the name does not already
 * carry it — "Agricultural area 2024", but "Rainfall 2025" rather than "Rainfall 2025
 * 2025", since the client's own name for C4 already names the year.
 */
export function rowLabel(t: Translate, dimension: string, period: string | number | null): string {
  const name = t(`intel.uc1.dimension.${dimension}`, { defaultValue: dimension });
  if (period == null) return name;
  const text = String(period);
  return name.includes(text) ? name : `${name} ${text}`;
}

/**
 * The count of watersheds a programme-wide fallback fell back from: the engine's own
 * `n_group`, which is on the driver of every fallback row. A row without it (an older
 * cached run) gets the wording that names no number at all, rather than a number scraped
 * out of a sentence.
 */
function fallbackCount(driver: IntelDriver): number | null {
  return typeof driver.n_group === 'number' ? driver.n_group : null;
}

/** The tail after the em dash: which third, or which absolute limit, the value was read against. */
function referenceTail(t: Translate, driver: IntelDriver, country: string): string | null {
  const kind = driver.reference_kind ?? null;
  const unit = driver.unit;

  if (kind === 'absolute') {
    if (Array.isArray(driver.reference) && driver.reference.length === 2) {
      return t('intel.uc1.row.outside', {
        lo: num(driver.reference[0]),
        hi: num(driver.reference[1]),
      });
    }
    if (driver.reference == null) return null;
    return t('intel.uc1.row.limit', { threshold: withUnit(driver.reference, unit) });
  }

  if (kind && kind.startsWith('tercile')) {
    // `rule_hit` names the class only for a unit that actually flagged; the definition's
    // own direction (a constant per constraint) answers for every other unit.
    const hit = driver.rule_hit ?? '';
    const cls = hit.endsWith('tercile_high')
      ? 'high'
      : hit.endsWith('tercile_low')
        ? 'low'
        : (UC1_TERCILE_CLASS[driver.dimension] ?? 'low');
    return t('intel.uc1.row.tercile', {
      third: t(cls === 'high' ? 'intel.uc1.row.highestThird' : 'intel.uc1.row.lowestThird'),
      country,
      cmp: cls === 'high' ? '≥' : '≤',
      boundary: withUnit(driver.reference, unit),
    });
  }

  return null;
}

/** Productivity's evidence line, from the driver's own `evidence[]`. */
function evidenceLine(t: Translate, driver: IntelDriver): string | undefined {
  const evidence = driver.evidence ?? [];
  const gaez = evidence.find((e) => e.metric_id === 'gaez_gapsh_main');
  if (gaez) return t('intel.uc1.evidence.productivity', { value: num(gaez.value) });
  // Launch round: C1's comparison line — ESA WorldCover 2021 cropland share beside the
  // Impact Observatory 2024 figure the rule scores (IO classes most Sahelian smallholder
  // land as rangeland, so its share reads far lower).
  const wc = evidence.find((e) => e.metric_id === 'cropland_share_wc');
  if (wc) return t('intel.uc1.evidence.ag_area', { value: num(wc.value) });
  return undefined;
}

/** Climate's 2025 anomaly line — "not yet available" until the 1991–2020 normal exists. */
function anomalyLine(t: Translate, values: Uc1Values): string {
  const anomaly = values.rain_anom_2025;
  return typeof anomaly === 'number'
    ? t('intel.uc1.anomaly.value', { value: num(anomaly) })
    : t('intel.uc1.anomaly.pending');
}

export interface BuildRowsArgs {
  t: Translate;
  drivers: IntelDriver[];
  values: Uc1Values;
  /** The unit's own ISO3, for "lowest third for Burkina Faso". */
  country: string | null;
  /** `admin0` rows read the roll-up share instead of a per-unit yes/no. */
  level: string;
}

/**
 * The five rows in C1…C5 order. A dimension the run did not serve is simply skipped, which
 * is what makes an old cached payload (the pre-launch dimension ids) render as fewer rows
 * rather than as five rows of nothing.
 */
export function buildUc1Rows({ t, drivers, values, country, level }: BuildRowsArgs): Uc1Row[] {
  const byDim = new Map(drivers.map((d) => [d.dimension, d]));
  const countryName = country
    ? t(`country.${country}`, { defaultValue: country })
    : t('intel.uc1.row.thisCountry');

  return UC1_DIMENSIONS.flatMap<Uc1Row>((dimension) => {
    const driver = byDim.get(dimension);
    if (!driver) return [];
    const label = rowLabel(t, dimension, driver.period);

    if (level === 'admin0') return [countryRow(t, dimension, label, values)];

    if (driver.flagged == null) {
      const reason = t(`intel.uc1.reason.${driver.reason ?? 'no_pixels'}`, {
        defaultValue: driver.reason ?? '',
      });
      return [
        {
          dimension,
          status: 'not_assessed',
          icon: ICONS.not_assessed,
          iconLabel: t('intel.uc1.row.status.notAssessed'),
          text: t('intel.uc1.row.notAssessedName', {
            name: t(`intel.uc1.dimension.${dimension}`, { defaultValue: dimension }),
          }),
          tail: t('intel.uc1.row.notAssessedReason', { reason }),
          ...extras(t, dimension, driver, values),
        },
      ];
    }

    const tail = referenceTail(t, driver, countryName);
    let text = tail
      ? t('intel.uc1.row.line', { name: label, value: withUnit(driver.value, driver.unit), tail })
      : t('intel.uc1.row.lineNoTail', {
          name: label,
          value: withUnit(driver.value, driver.unit),
        });
    if (driver.reference_kind === 'tercile_programme_fallback') {
      const n = fallbackCount(driver);
      text += ` — ${
        n == null
          ? t('intel.uc1.row.programmeFallbackNoCount', { country: countryName })
          : t('intel.uc1.row.programmeFallback', { country: countryName, n })
      }`;
    }

    return [
      {
        dimension,
        status: driver.flagged ? 'flagged' : 'not_flagged',
        icon: driver.flagged ? ICONS.flagged : ICONS.not_flagged,
        iconLabel: driver.flagged
          ? t('intel.uc1.row.status.flagged')
          : t('intel.uc1.row.status.notFlagged'),
        text,
        ...extras(t, dimension, driver, values),
      },
    ];
  });
}

/** The two per-constraint extra lines the client's §5 names, for whichever row owns them. */
function extras(
  t: Translate,
  dimension: string,
  driver: IntelDriver,
  values: Uc1Values,
): Pick<Uc1Row, 'evidence' | 'subLine'> {
  if (dimension === 'productivity' || dimension === 'ag_area') {
    return { evidence: evidenceLine(t, driver) };
  }
  if (dimension === 'climate') return { subLine: anomalyLine(t, values) };
  return {};
}

/**
 * A country's row: the share of its watersheds flagged for that constraint, out of the
 * ones that could be assessed at all (the engine's own denominator, Task 1 fix round 1).
 * There is no tick or cross to give — a country is not flagged, its watersheds are.
 */
function countryRow(t: Translate, dimension: string, label: string, values: Uc1Values): Uc1Row {
  const share = values.rollup?.share_flagged?.[dimension];
  const assessed = values.rollup?.n_assessed?.[dimension];
  const name = t(`intel.uc1.dimension.${dimension}`, { defaultValue: dimension });
  if (typeof share !== 'number') {
    return {
      dimension,
      status: 'not_assessed',
      icon: ICONS.not_assessed,
      iconLabel: t('intel.uc1.row.status.notAssessed'),
      text: t('intel.uc1.row.notAssessedName', { name }),
      tail: t('intel.uc1.row.countryNotAssessedTail'),
    };
  }
  return {
    dimension,
    status: share > 0 ? 'flagged' : 'not_flagged',
    icon: share > 0 ? ICONS.flagged : ICONS.not_flagged,
    iconLabel: share > 0 ? t('intel.uc1.row.status.flagged') : t('intel.uc1.row.status.notFlagged'),
    text:
      typeof assessed === 'number'
        ? t('intel.uc1.row.countryShare', {
            name: label,
            share: num(share * 100, 0),
            assessed,
          })
        : t('intel.uc1.row.countryShareNoCount', { name: label, share: num(share * 100, 0) }),
  };
}
